import { INFOODS, NutrientTarget } from './food';

/**
 * Nutrient prepared for display to the user.
 *
 * `T` is the type of the display TEXT. It exists so the caller's string type survives the trip
 * through this shared module: the client builds these with LL-backed translations, whose calls
 * return the branded `TranslatedString`, so it gets `DisplayNutrient<TranslatedString>` back and
 * the text stays type-checked all the way to the template. A caller with no translations (a unit
 * test, or any non-client caller) gets plain `string`. Shared code cannot import the client-only
 * brand itself, which is why this is a type parameter rather than a fixed type (#4368).
 */
export interface DisplayNutrient<T extends string = string> {
  tag: string /** Original INFOODS tag for the nutrient.  */;
  icon?: string;
  name: T /** Descriptive name */;
  shortName?: T;
  threeLetterName?: T;
  value?: number /** Value of the nutrient */;
  valueShort: string /** Text with value for condensed displays. May contain limit in some cases.  */;
  valueLong: string /** Text with value for expanded displays. */;
  valueUnit?: string /** Unit of the value */;
  valueError?:
    | string
    | ''
    | undefined /** Text with how much value is over/under for expanded displays. Empty string if all is ok. undefined if no target for this nutrient */;
  valueLimit?: string /** Text with description of the limits - used in expanded displays. */;
  target?: NutrientTarget;
  progress_pc?: number /** Percentage of progress towards the goal. */;
  limitCSS?: 'under' | 'over' | 'okay' | 'ignored' /** CSS class name, signifies if the limit is satisfied or not and in which way. */;
  unitId: string /** Used in code identifiers (so use e.g. "ug", not "µg") */;
  required?: boolean /** Food cannot be created without declaring a value of this nutrient. */;
  displayInHeader?: boolean;
  tracked?: boolean;
}

/**
 * One nutrient's stable, text-free definition.
 *
 * The display NAMES used to live here as English literals. They moved into the client's
 * translation catalog, because they are ordinary UI copy that every user should read in their own
 * language -- what is left here is the part that is the same in every language (#4368).
 */
export interface NutrientDefinition {
  tag: string;
  icon?: string;
  unitId: string;
  required?: boolean;
  displayInHeader?: boolean;
  tracked?: boolean;
}

/**
 * Looks up a nutrient's display words by its INFOODS tag. Supplied by the caller, because shared
 * code cannot reach the client's `LL`.
 *
 * `shortName` and `threeLetterName` return `undefined` for the many nutrients that have no
 * abbreviated form -- only the handful shown on the macro cards do.
 */
export interface NutrientTexts<T extends string = string> {
  name: (tag: string) => T;
  shortName: (tag: string) => T | undefined;
  threeLetterName: (tag: string) => T | undefined;
}

/** Every nutrient the app can display, in the order the UI should list them. */
export const NutrientDefinitionsOrdered: NutrientDefinition[] = [
  { tag: INFOODS.Calories, icon: 'flame', unitId: 'kcal', required: true, displayInHeader: true, tracked: true },
  { tag: INFOODS.Protein, unitId: 'g', required: true, displayInHeader: true, tracked: true },
  { tag: INFOODS.Fat, unitId: 'g', required: true, displayInHeader: true, tracked: true },
  { tag: INFOODS.CarbNet, unitId: 'g', required: true, displayInHeader: true, tracked: true },
  { tag: INFOODS.Fiber, unitId: 'g', required: true, displayInHeader: true, tracked: true },
  { tag: INFOODS.Cholesterol, unitId: 'mg', tracked: true },
  { tag: 'FASAT_g', unitId: 'g' },
  { tag: 'FAMS_g', unitId: 'g' },
  { tag: 'FAPU_g', unitId: 'g' },
  { tag: 'FAPUN3_g', unitId: 'g' },
  { tag: 'FAPUN6_g', unitId: 'g' },
  { tag: 'FAPUN9_g', unitId: 'g' },
  { tag: 'FATRN_g', unitId: 'g' },
  { tag: INFOODS.CarbTot, unitId: 'g' },
  { tag: 'SUGAR_g', unitId: 'g' },
  { tag: 'SUGAD_g', unitId: 'g' },
  { tag: 'STARCH_g', unitId: 'g' },
  { tag: 'NACL_mg', unitId: 'mg' },
  { tag: 'ALC_g', unitId: 'g' },
  { tag: 'WATER_g', unitId: 'g' },
  { tag: 'CAFFN_mg', unitId: 'mg' },
  { tag: 'THEBRN_mg', unitId: 'mg' },
  { tag: 'CA_mg', unitId: 'mg' },
  { tag: 'FE_mg', unitId: 'mg' },
  { tag: 'MG_mg', unitId: 'mg' },
  { tag: 'P_mg', unitId: 'mg' },
  { tag: 'K_mg', unitId: 'mg' },
  { tag: 'NA_mg', unitId: 'mg' },
  { tag: 'ZN_mg', unitId: 'mg' },
  { tag: 'CU_mg', unitId: 'mg' },
  { tag: 'FLD_ug', unitId: 'ug' },
  { tag: 'MN_mg', unitId: 'mg' },
  { tag: 'SE_ug', unitId: 'ug' },
  { tag: 'ID_ug', unitId: 'ug' },
  { tag: 'VITA_IU_IU', unitId: 'IU' },
  { tag: 'RETOL_ug', unitId: 'ug' },
  { tag: 'VITA_RAE_ug', unitId: 'ug' },
  { tag: 'CARTB_ug', unitId: 'ug' },
  { tag: 'CARTA_ug', unitId: 'ug' },
  { tag: 'TOCPHA_mg', unitId: 'mg' },
  { tag: 'VITD_ug', unitId: 'IU' },
  { tag: 'ERGCAL_ug', unitId: 'ug' },
  { tag: 'CHOCAL_ug', unitId: 'ug' },
  { tag: 'VITC_mg', unitId: 'mg' },
  { tag: 'THIA_mg', unitId: 'mg' },
  { tag: 'RIBF_mg', unitId: 'mg' },
  { tag: 'NIA_mg', unitId: 'mg' },
  { tag: 'PANTAC_mg', unitId: 'mg' },
  { tag: 'VITB6A_mg', unitId: 'mg' },
  { tag: 'FOL_ug', unitId: 'ug' },
  { tag: 'VITB12_ug', unitId: 'ug' },
  { tag: 'CHOLN_mg', unitId: 'mg' },
  { tag: 'VITK1_ug', unitId: 'ug' },
  { tag: 'FOLAC_ug', unitId: 'mg' },
  { tag: 'ASH_g', unitId: 'g' },
  { tag: 'BIOT_ug', unitId: 'ug' },
  { tag: 'CR_ug', unitId: 'ug' },
  { tag: 'MO_ug', unitId: 'ug' },
  { tag: 'F18D2CN6_g', unitId: 'g' },
  { tag: 'F18D3N3_g', unitId: 'g' },
  { tag: 'F20D5_g', unitId: 'g' },
  { tag: 'F22D6N3_g', unitId: 'g' },
  { tag: 'F20D4_g', unitId: 'g' },
  { tag: 'SORTL_mg', unitId: 'mg' },
  { tag: 'MALTL_mg', unitId: 'mg' },
  { tag: 'MANIT_mg', unitId: 'mg' },
  { tag: 'ISOMT_mg', unitId: 'mg' },
  { tag: 'LACTIT_mg', unitId: 'mg' },
  { tag: 'XILIT_mg', unitId: 'mg' },
  { tag: 'ERITR_mg', unitId: 'mg' },
  { tag: 'POLYL_mg', unitId: 'mg' },
];

/**
 * The nutrient list with display text filled in from the caller's translations.
 *
 * Returns `Partial` entries because the value fields (`valueShort`, `valueLong`, ...) are filled
 * in later, per food, by the client's `FormatNutrients`.
 */
export function buildDefaultDisplayNutrients<T extends string>(
  texts: NutrientTexts<T>,
): Partial<DisplayNutrient<T>>[] {
  return NutrientDefinitionsOrdered.map(definition => ({
    ...definition,
    name: texts.name(definition.tag),
    shortName: texts.shortName(definition.tag),
    threeLetterName: texts.threeLetterName(definition.tag),
  }));
}
